import "./AiFormeLandingPage.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { AiForMeHeaderBanner } from "common/components/AIForMe/AiForMeHeaderBanner";
import { CompareBar, CompareFlyout } from "common/components/AIForMe/Compare";
import { useAiForMeSearchList } from "common/components/AIForMe/useAiForMeSearchList";
import { ModalWhatsNew } from "common/components/ModalWhatsNew/ModalWhatsNew";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { CompareProvider, useCompare } from "common/context/compare";
import { useLists } from "common/context/lists/ListsContext";
import { createTrackClickInteraction, fetchRecommendedTools, getJobFamilyNameFromMap } from "common/helpers";
import { useAiForMeFeedbacks } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { CSRFService } from "lib/services/csrf-service";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { FeaturedToolsSection } from "./components/FeaturedToolsSection";
import { RecentlyUsedSection } from "./components/RecentlyUsedSection";
import { TrendingToolsSection } from "./components/TrendingToolsSection";

type SortOption = {
  field: string;
  order: "asc" | "desc";
};

const sortingOption: SortOption = { field: "creationDate", order: "desc" };

// temporary get items to fill data
/* istanbul ignore next */
const GetItems = async (
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  client: CatalogicalService<AIForMeTool>
) => {
  setIsLoading(true);
  try {
    const response = await client.getItems({
      sortBy: [sortingOption.field],
      sortDirection: [sortingOption.order],
      filters: {},
    });
    setIsLoading(false);

    return response;
  } catch (err) {
    setIsLoading(false);
  }
};

const showLoadingComponent = (loadingMessagePathString: string) => {
  return (
    <Col alignItems="center" justifyContent="center" minHeight="400px">
      <Spinner size={SpinnerSize.Small} />
      <Text fontSize={"S100"}>{getString(loadingMessagePathString)}</Text>
    </Col>
  );
};

/**
 * Inner component that uses compare context
 */
const AiFormeLandingPageContent: FC = () => {
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const { inputs, updateInputs, batchUpdateInputs } = useAiForMeSearchList();
  const [isLoadingTemporaryTool, setIsLoadingTemporaryTool] = useState<boolean>(true);
  const [isLoadingRoleTools, setIsLoadingRoleTools] = useState<boolean>(true);
  const catalogicalServiceClient = useMemo(() => new CatalogicalService<AIForMeTool>("aiforme"), []);
  const csrfService = useMemo(() => new CSRFService(), []);
  const [jobFamilyName, setJobFamilyName] = useState<string | undefined>();
  const { evaluateLists } = useLists();

  // Role tools from evaluateLists based on job family
  const [roleTools, setRoleTools] = useState<AIForMeTool[]>([]);

  useEffect(() => {
    const fetchJobFamily = async () => {
      try {
        const name = await getJobFamilyNameFromMap();
        setJobFamilyName(name);
      } catch (err) {
        setError("Failed to load user information");
        console.error("Error fetching job family:", err);
      }
    };
    fetchJobFamily();
  }, []);

  // Fetch role tools using evaluateLists when jobFamilyName is available
  useEffect(() => {
    if (jobFamilyName && evaluateLists) {
      setIsLoadingRoleTools(true);
      fetchRecommendedTools([jobFamilyName], evaluateLists, "All")
        .then((tools) => {
          setRoleTools(tools as AIForMeTool[]);
        })
        .catch((error) => {
          console.error("Error fetching role tools:", error);
          setRoleTools([]);
        })
        .finally(() => {
          setIsLoadingRoleTools(false);
        });
    } else if (!jobFamilyName) {
      setIsLoadingRoleTools(false);
    }
  }, [jobFamilyName, evaluateLists]);

  const { userFeedbacks, voteHandler } = useAiForMeFeedbacks(catalogicalServiceClient, csrfService);

  // Compare functionality
  const { selectedTools, canCompare, isFlyoutOpen, openFlyout, closeFlyout, clearAll, removeTool } = useCompare();

  const trackClickInteraction = useMemo(
    () => createTrackClickInteraction(catalogicalServiceClient, csrfService),
    [catalogicalServiceClient, csrfService]
  );

  // Temporary: using all tools for orgTools and amazonPickedTools during scaffolding.
  // TODO: Replace with evaluateLists calls when org and amazon recommended data is available.
  /* istanbul ignore next */
  const [temporaryTools, setTemporaryAllTools] = useState<AIForMeTool[] | []>([]);
  useEffect(() => {
    GetItems(setIsLoadingTemporaryTool, catalogicalServiceClient).then((response) => {
      if (response) {
        setTemporaryAllTools(response.data || []);
      }
    });
  }, [catalogicalServiceClient]);

  return (
    <Col className="aiforme-landing-page">
      <div className="aiforme-landing-page__gradient-back">
        <ModalWhatsNew />
        <PageMainContainer>
          <Row>
            <Breadcrumbs
              crumbs={[
                {
                  title: getString("logoText"),
                  url: "/",
                },
                {
                  title: getString("aiForMe.title"),
                },
              ]}
              onCrumbClick={
                /* istanbul ignore next */
                (e, crumb: CrumbProps) => {
                  if (crumb.url) {
                    e.preventDefault();
                    history.push(crumb.url);
                  }
                }
              }
            />
          </Row>
          <AiForMeHeaderBanner
            inputs={inputs}
            updateInputs={updateInputs}
            batchUpdateInputs={batchUpdateInputs}
          ></AiForMeHeaderBanner>
        </PageMainContainer>
      </div>
      <div className="aiforme-landing-page__content">
        <PageMainContainer>
          {isLoadingTemporaryTool || isLoadingRoleTools ? (
            showLoadingComponent("aiForMe.landingPage.featuredToolsSection.loadingMessage")
          ) : (
            <Col margin={"3rem 0"}>
              <FeaturedToolsSection
                roleTools={roleTools}
                orgTools={temporaryTools.slice(0, 8)} // TODO: Replace with evaluateLists for org tools
                amazonPickedTools={temporaryTools} // TODO: Replace with evaluateLists for amazon recommended tools
                userRole={jobFamilyName || ""}
                voteHandler={voteHandler}
                client={catalogicalServiceClient}
                onTrackClick={trackClickInteraction}
              />
            </Col>
          )}

          {isLoadingTemporaryTool ? (
            showLoadingComponent("aiForMe.landingPage.recentlyUsedToolsSection.loadingMessage")
          ) : (
            <Col margin={"3rem 0"}>
              <RecentlyUsedSection
                tools={temporaryTools.map((tool) => ({
                  ...tool,
                  lastUsedDate: new Date().toISOString(),
                }))}
                voteHandler={voteHandler}
                client={catalogicalServiceClient}
                userFeedbacks={userFeedbacks}
                onTrackClick={trackClickInteraction}
              />
            </Col>
          )}

          {isLoadingTemporaryTool ? (
            showLoadingComponent("aiForMe.landingPage.weeklyTrendingToolsSection.loadingMessage")
          ) : (
            <Col margin={"3rem 0"}>
              <TrendingToolsSection
                tools={temporaryTools.slice(0, 9)} // TODO: Replace with trending tools (when available)
                voteHandler={voteHandler}
                client={catalogicalServiceClient}
                onTrackClick={trackClickInteraction}
              />
            </Col>
          )}
        </PageMainContainer>
      </div>

      {/* Compare Bar - sticky bottom banner */}
      <CompareBar
        selectedTools={selectedTools}
        canCompare={canCompare}
        isComparing={isFlyoutOpen}
        onRemoveTool={removeTool}
        onCancel={clearAll}
        onCompareNow={openFlyout}
      />

      {/* Compare Flyout - modal with comparison table */}
      <CompareFlyout isOpen={isFlyoutOpen} tools={selectedTools} onClose={closeFlyout} onRemoveTool={removeTool} />
    </Col>
  );
};

/**
 * New AI for Me landing page with personalized experience.
 * Controlled by the TOGGLE_AI_FOR_ME_LANDING_PAGE feature flag.
 */
const AiFormeLandingPage: FC = () => {
  return (
    <CompareProvider>
      <AiFormeLandingPageContent />
    </CompareProvider>
  );
};

export default AiFormeLandingPage;
